import { cn } from '@natu/utils/cn';

import {
  type AlignItems,
  type BorderRadius,
  type FontFamily,
  type FontWeight,
  type Grid,
  type JustifyItems,
  type Size,
  type Spacing,
  type TextAlign,
  type Elevation,
  type MaxHeight,
  alignItemsDesktopVariants,
  alignItemsTabletVariants,
  alignItemsVariants,
  fontFamilyVariants,
  fontWeightVariants,
  gapDesktopVariants,
  gapMobileVariants,
  gapTabletVariants,
  gridDesktopVariants,
  gridMobileVariants,
  gridTabletVariants,
  justifyItemsDesktopVariants,
  justifyItemsTabletVariants,
  justifyItemsVariants,
  mbDesktopVariants,
  mbTabletVariants,
  mbVariants,
  mtDesktopVariants,
  mtTabletVariants,
  mtVariants,
  negativeMlDesktopVariants,
  negativeMlTabletVariants,
  negativeMlVariants,
  pbDesktopVariants,
  pbMobileVariants,
  pbTabletVariants,
  plDesktopVariants,
  plMobileVariants,
  plTabletVariants,
  prDesktopVariants,
  prMobileVariants,
  prTabletVariants,
  ptDesktopVariants,
  ptMobileVariants,
  ptTabletVariants,
  sizeVariants,
  textAlignDesktopVariants,
  textAlignTabletVariants,
  textAlignVariants,
  topNotchVariants,
  topNotchTabletVariants,
  topNotchDesktopVariants,
  rtlVariants,
  rtlVariantsTablet,
  rtlVariantsDesktop,
  rtrVariants,
  rtrVariantsTablet,
  rtrVariantsDesktop,
  rblVariants,
  rblVariantsTablet,
  rblVariantsDesktop,
  rbrVariants,
  rbrVariantsTablet,
  rbrVariantsDesktop,
  radiusVariants,
  radiusVariantsTablet,
  radiusVariantsDesktop,
  elevationVariants,
  elevationVariantsTablet,
  elevationVariantsDesktop,
  maxHeightVariants,
} from './styles';

// Func props
interface ResolveStoryblokStylesProps {
  // fonts
  fontFamily?: FontFamily;
  fontWeight?: FontWeight;
  textAlign?: TextAlign;
  textAlignTablet?: TextAlign;
  textAlignDesktop?: TextAlign;

  // flex
  justifyItems?: JustifyItems;
  justifyItemsTablet?: JustifyItems;
  justifyItemsDesktop?: JustifyItems;
  alignItems?: AlignItems;
  alignItemsTablet?: AlignItems;
  alignItemsDesktop?: AlignItems;

  // grid
  gridMobile?: Grid;
  gridTablet?: Grid;
  gridDesktop?: Grid;

  // gap
  gap?: Spacing;
  gapTablet?: Spacing;
  gapDesktop?: Spacing;

  // top notch
  topNotch?: Spacing;
  topNotchTablet?: Spacing;
  topNotchDesktop?: Spacing;

  // size
  size?: Size;

  // margin
  mb?: Spacing;
  mbTablet?: Spacing;
  mbDesktop?: Spacing;

  mt?: Spacing;
  mtTablet?: Spacing;
  mtDesktop?: Spacing;

  mlMinus?: Spacing;
  mlMinusTablet?: Spacing;
  mlMinusDesktop?: Spacing;

  // padding
  pl?: Spacing;
  plTablet?: Spacing;
  plDesktop?: Spacing;

  pr?: Spacing;
  prTablet?: Spacing;
  prDesktop?: Spacing;

  pt?: Spacing;
  ptTablet?: Spacing;
  ptDesktop?: Spacing;

  pb?: Spacing;
  pbTablet?: Spacing;
  pbDesktop?: Spacing;

  // radius
  rtlMobile?: BorderRadius;
  rtlTablet?: BorderRadius;
  rtlDesktop?: BorderRadius;
  rtrMobile?: BorderRadius;
  rtrTablet?: BorderRadius;
  rtrDesktop?: BorderRadius;
  rblMobile?: BorderRadius;
  rblTablet?: BorderRadius;
  rblDesktop?: BorderRadius;
  rbrMobile?: BorderRadius;
  rbrTablet?: BorderRadius;
  rbrDesktop?: BorderRadius;
  rounded?: BorderRadius;
  roundedTablet?: BorderRadius;
  roundedDesktop?: BorderRadius;

  // elevation (shadows)
  elevation?: Elevation;
  elevationTablet?: Elevation;
  elevationDesktop?: Elevation;

  // maxHeight
  maxHeight?: MaxHeight;

  // extra styles
  className?: string;
}

export const resolveStoryblokStyles = ({
  // fonts
  fontFamily,
  fontWeight,
  textAlign,
  textAlignTablet,
  textAlignDesktop,

  // flex
  justifyItems,
  justifyItemsTablet,
  justifyItemsDesktop,
  alignItems,
  alignItemsTablet,
  alignItemsDesktop,

  // grid
  gridMobile,
  gridTablet,
  gridDesktop,

  // gap
  gap,
  gapTablet,
  gapDesktop,

  // top notch
  topNotch,
  topNotchTablet,
  topNotchDesktop,

  // container size - max-w
  size,

  // margin
  mb,
  mbTablet,
  mbDesktop,

  mt,
  mtTablet,
  mtDesktop,

  mlMinus,
  mlMinusTablet,
  mlMinusDesktop,

  // padding
  pl,
  plTablet,
  plDesktop,

  pr,
  prTablet,
  prDesktop,

  pt,
  ptTablet,
  ptDesktop,

  pb,
  pbTablet,
  pbDesktop,

  // border radius
  rtlMobile,
  rtlTablet,
  rtlDesktop,
  rtrMobile,
  rtrTablet,
  rtrDesktop,
  rblMobile,
  rblTablet,
  rblDesktop,
  rbrMobile,
  rbrTablet,
  rbrDesktop,
  rounded,
  roundedTablet,
  roundedDesktop,

  // elevation
  elevation,
  elevationTablet,
  elevationDesktop,

  maxHeight,

  className,
}: ResolveStoryblokStylesProps) =>
  cn(
    // fonts
    fontFamily && fontFamilyVariants[fontFamily],
    fontWeight && fontWeightVariants[fontWeight],
    textAlign && textAlignVariants[textAlign],
    textAlignTablet && textAlignTabletVariants[textAlignTablet],
    textAlignDesktop && textAlignDesktopVariants[textAlignDesktop],

    // flex
    justifyItems && justifyItemsVariants[justifyItems],
    justifyItemsTablet && justifyItemsTabletVariants[justifyItemsTablet],
    justifyItemsDesktop && justifyItemsDesktopVariants[justifyItemsDesktop],
    alignItems && alignItemsVariants[alignItems],
    alignItemsTablet && alignItemsTabletVariants[alignItemsTablet],
    alignItemsDesktop && alignItemsDesktopVariants[alignItemsDesktop],

    // grid
    gridMobile && gridMobileVariants[gridMobile],
    gridTablet && gridTabletVariants[gridTablet],
    gridDesktop && gridDesktopVariants[gridDesktop],

    // gap
    gap && gapMobileVariants[gap],
    gapTablet && gapTabletVariants[gapTablet],
    gapDesktop && gapDesktopVariants[gapDesktop],

    // top notch
    topNotch && topNotchVariants[topNotch],
    topNotchTablet && topNotchTabletVariants[topNotchTablet],
    topNotchDesktop && topNotchDesktopVariants[topNotchDesktop],

    // size
    size && sizeVariants[size],

    // spacing
    mb && mbVariants[mb],
    mbTablet && mbTabletVariants[mbTablet],
    mbDesktop && mbDesktopVariants[mbDesktop],

    mt && mtVariants[mt],
    mtTablet && mtTabletVariants[mtTablet],
    mtDesktop && mtDesktopVariants[mtDesktop],

    mlMinus && negativeMlVariants[mlMinus],
    mlMinusTablet && negativeMlTabletVariants[mlMinusTablet],
    mlMinusDesktop && negativeMlDesktopVariants[mlMinusDesktop],

    // padding
    pl && plMobileVariants[pl],
    plTablet && plTabletVariants[plTablet],
    plDesktop && plDesktopVariants[plDesktop],

    pr && prMobileVariants[pr],
    prTablet && prTabletVariants[prTablet],
    prDesktop && prDesktopVariants[prDesktop],

    pt && ptMobileVariants[pt],
    ptTablet && ptTabletVariants[ptTablet],
    ptDesktop && ptDesktopVariants[ptDesktop],

    pb && pbMobileVariants[pb],
    pbTablet && pbTabletVariants[pbTablet],
    pbDesktop && pbDesktopVariants[pbDesktop],

    // border radius
    rtlMobile && rtlVariants[rtlMobile],
    rtlTablet && rtlVariantsTablet[rtlTablet],
    rtlDesktop && rtlVariantsDesktop[rtlDesktop],
    rtrMobile && rtrVariants[rtrMobile],
    rtrTablet && rtrVariantsTablet[rtrTablet],
    rtrDesktop && rtrVariantsDesktop[rtrDesktop],
    rblMobile && rblVariants[rblMobile],
    rblTablet && rblVariantsTablet[rblTablet],
    rblDesktop && rblVariantsDesktop[rblDesktop],
    rbrMobile && rbrVariants[rbrMobile],
    rbrTablet && rbrVariantsTablet[rbrTablet],
    rbrDesktop && rbrVariantsDesktop[rbrDesktop],
    rounded && radiusVariants[rounded],
    roundedTablet && radiusVariantsTablet[roundedTablet],
    roundedDesktop && radiusVariantsDesktop[roundedDesktop],

    // elevation
    elevation && elevationVariants[elevation],
    elevationTablet && elevationVariantsTablet[elevationTablet],
    elevationDesktop && elevationVariantsDesktop[elevationDesktop],

    maxHeight && maxHeightVariants[maxHeight],

    className,
  );
